import { daysSince, isOverdue, isTaskOverdue, localDate, todayIso } from './dealFormat'

/**
 * A deal's follow-up health — an at-a-glance read of whether the next touch is under control.
 * Computed on the client from the same facts the snapshot already shows (next contact date, open
 * tasks, time in stage). The dashboard counts and ranks the whole pipeline by the same rules on the
 * server (backend `deal/DealHealth.java`) — change the two together, so a deal reads the same
 * on its card as in the dashboard.
 */
export type DealHealthKey = 'overdue' | 'due_soon' | 'no_follow_up' | 'stalled' | 'on_track'

export type HealthTone = 'red' | 'amber' | 'gray' | 'green'

export interface DealHealth {
  key: DealHealthKey
  label: string
  tone: HealthTone
}

/** A stage this deal has sat in longer than this (days) with no urgent follow-up reads as stalled. */
const STALLED_DAYS = 30
/** A follow-up falling within this many days from today counts as "due soon". */
const DUE_SOON_DAYS = 3

/** Open (not yet completed) task with a due date. */
export interface HealthTask {
  dueAt: string | null
  completedAt: string | null
}

/** Label and tone per state — the one vocabulary for health, shared with the dashboard. */
export const HEALTH_META: Record<DealHealthKey, { label: string; tone: HealthTone }> = {
  overdue: { label: 'Overdue', tone: 'red' },
  due_soon: { label: 'Due soon', tone: 'amber' },
  no_follow_up: { label: 'No follow-up', tone: 'gray' },
  stalled: { label: 'Stalled', tone: 'amber' },
  on_track: { label: 'On track', tone: 'green' },
}

function withinDays(iso: string, days: number): boolean {
  const target = localDate(iso.includes('T') ? iso : `${iso}T00:00:00`)
  const from = todayIso()
  if (target < from) return false
  const limit = new Date()
  limit.setDate(limit.getDate() + days)
  const to = `${limit.getFullYear()}-${String(limit.getMonth() + 1).padStart(2, '0')}-${String(limit.getDate()).padStart(2, '0')}`
  return target <= to
}

export function dealHealth({
  nextContactDate,
  stageEnteredAt,
  tasks,
}: {
  nextContactDate: string | null
  stageEnteredAt: string
  tasks: HealthTask[]
}): DealHealth {
  const openTasks = tasks.filter((t) => !t.completedAt && t.dueAt)

  const anyOverdue =
    (nextContactDate != null && isOverdue(nextContactDate)) ||
    openTasks.some((t) => t.dueAt != null && isTaskOverdue(t.dueAt))
  if (anyOverdue) return { key: 'overdue', ...HEALTH_META.overdue }

  const anyDueSoon =
    (nextContactDate != null && withinDays(nextContactDate, DUE_SOON_DAYS)) ||
    openTasks.some((t) => t.dueAt != null && withinDays(t.dueAt, DUE_SOON_DAYS))
  if (anyDueSoon) return { key: 'due_soon', ...HEALTH_META.due_soon }

  if (nextContactDate == null && openTasks.length === 0) {
    return { key: 'no_follow_up', ...HEALTH_META.no_follow_up }
  }

  if (daysSince(stageEnteredAt) > STALLED_DAYS) return { key: 'stalled', ...HEALTH_META.stalled }

  return { key: 'on_track', ...HEALTH_META.on_track }
}

/** Tailwind classes for a health badge (background + text) keyed by tone. */
export const HEALTH_BADGE: Record<HealthTone, string> = {
  red: 'bg-red-50 text-red-600',
  amber: 'bg-amber-50 text-amber-600',
  gray: 'bg-gray-100 text-gray-500',
  green: 'bg-emerald-50 text-emerald-600',
}

/** Tailwind class for a small solid health dot keyed by tone. */
export const HEALTH_DOT: Record<HealthTone, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  gray: 'bg-gray-400',
  green: 'bg-emerald-500',
}
